import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import {
  AllocatedTimeDTO,
  IncidenceTableDto,
  MaintenanceOrderDetailsDto,
  MaterialTableDto,
} from 'apps/marsinet/src/app/typescript-axios';
import { getIncidentStatusColor } from '../../commons/rps-incidence-helpers';
import { t } from 'i18next';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { formatUtcTimestamp } from 'apps/marsinet/src/app/utils/timezone-utils';

/* -------------------------------------------------------------------------- */
/* Tablas                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Todas las listas del detalle usan el mismo formato que la tabla de OMs del
 * detalle de incidencia: sin filtros ni ordenación, paginador sólo cuando la
 * lista no cabe de un vistazo.
 */

const TABLE_CLASS = 'om-table';
const LABEL_CLASS = 'text-sm font-semibold text-900 mb-1';
const VALUE_CLASS = 'text-base text-700 line-height-3';
const EMPTY_CLASS = 'text-500 italic';
const ROWS_PER_PAGE = 5;

const EmptyValue = () => {
  const { t } = useTranslation();
  return (
    <span className={EMPTY_CLASS}>
      {t('pages.maintenance_orders.no_data')}
    </span>
  );
};

const isEmptyValue = (value: unknown) =>
  value === undefined || value === null || value === '';

const textCell = (value?: string | number | null): React.ReactNode =>
  isEmptyValue(value) ? <EmptyValue /> : value;

/** Máximo 2 líneas; el texto completo se ve al pasar el ratón. */
const clampedCell = (value?: string | null) =>
  !value ? (
    <span className="text-500 italic">{t('pages.maintenance_orders.no_data')}</span>
  ) : (
    <div
      title={value}
      style={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        overflowWrap: 'anywhere',
      }}
    >
      {value}
    </div>
  );

/** Para emails: se parten en varias líneas en vez de cortarse. */
const breakAnywhereCell = (value?: string | null) =>
  !value ? (
    <span className="text-500 italic">{t('pages.maintenance_orders.no_data')}</span>
  ) : (
    <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{value}</div>
  );

/** Texto que puede ocupar varias líneas sin cortarse. */
const wrapCell = (value?: string | number | null): React.ReactNode =>
  isEmptyValue(value) ? (
    <EmptyValue />
  ) : (
    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{value}</div>
  );

// `date` llega como Date en unos DTOs generados y como string en otros, según
// cómo esté declarado el @ApiProperty: se aceptan los dos.
const formatDateTimeText = (
  value: string | Date | null | undefined,
  timeZone: string,
  locale: string
): string | undefined => {
  if (!value) return undefined;

  // Muestra los instantes UTC de la API en la zona elegida sin cambiar los datos guardados.
  return formatUtcTimestamp(value, timeZone, locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const actionBodyTemplate = (
  rowData: any,
  handleAssignMaterial,
  assigningMaterial,
  maintenanceOrder,
  t
) => (
  <Button
    icon="pi pi-plus"
    className="p-button-rounded p-button-success"
    onClick={() => handleAssignMaterial(rowData)}
    loading={assigningMaterial}
    disabled={!maintenanceOrder?.id}
    title={t('pages.maintenance_orders.assign_material')}
  />
);

const articleNameTemplate = (rowData: any) => {
  return (
    `${rowData?.Item?.MainDescriptor || ''} ${rowData?.Item?.SecondaryDescriptor || ''}`.trim() ||
    t('emptyMessage')
  );
};
const articleIdTemplate = (rowData: any) => {
  return rowData?.Item?.EntityID || t('emptyMessage');
};

const formatPrice = (price: any): string => {
  if (!price) return t('emptyMessage');

  const value = price.Value ?? 0;
  const decimalPlaces = price.DecimalPlaces ?? 2;
  const suffix = price.Suffix || '';

  const formatted = Number(value).toFixed(decimalPlaces);
  return `${formatted} ${suffix}`.trim();
};

const costTemplate = (rowData: any) => textCell(formatPrice(rowData?.Price));

// Sin el `?.` en Stock, un artículo sin stock rompe la tabla entera.
const quantityAvailableTemplate = (rowData: any) =>
  textCell(rowData?.Stock?.Value);


const seriesTemplate = (rowData: any, selectedSeries, setSelectedSeries) => {
  if (!rowData?.series || rowData.series.length === 0) {
    return t('emptyMessage');
  }

  const articleKey = rowData?.Item?.EntityID;
  const currentSelection = selectedSeries[articleKey];

  return (
    <Dropdown
      value={currentSelection}
      options={rowData.series}
      optionLabel="Item.MainDescriptor"
      onChange={(e) =>
        setSelectedSeries({
          ...selectedSeries,
          [articleKey]: e.value,
        })
      }
      placeholder="Seleccionar serie"
      className="w-full"
      showClear={!!currentSelection}
    />
  );
};

interface MaterialsToAsignProps {
  selectedSeries: any;
  setSelectedSeries: any;
  loadingArticles: any;
  articles: any;
  handleAssignMaterial: any;
  assigningMaterial: any;
  maintenanceOrder: any;
}

export const MaterialsToAsign = ({
  selectedSeries,
  setSelectedSeries,
  loadingArticles,
  articles,
  handleAssignMaterial,
  assigningMaterial,
  maintenanceOrder,
}: MaterialsToAsignProps) => {
  const { t } = useTranslation();
  return (
    <div className="col-12 surface-card border-round-xl shadow-3 p-4 mb-4">
      <h4 className="text-lg font-semibold text-900 mt-0 mb-3">{t('pages.maintenance_orders.articles_to_assign')}</h4>
      {loadingArticles ? (
        <div className="text-center p-4">
          <span className="pi pi-spin pi-spinner"></span>
        </div>
      ) : articles.length > 0 ? (
        <DataTable
          value={articles}
          responsiveLayout="scroll"
          size="small"
          className={TABLE_CLASS}
        >
          <Column
            field="Item.Description"
            header={t('pages.maintenance_orders.article')}
            body={articleNameTemplate}
          />
          <Column field="Item.EntityID" header={t('pages.maintenance_orders.id')} body={articleIdTemplate} />
          <Column field="Item.Cost" header={t('pages.maintenance_orders.cost')} body={costTemplate} />
          <Column
            field="series"
            header={t('pages.maintenance_orders.select_series')}
            body={(rowData) =>
              seriesTemplate(rowData, selectedSeries, setSelectedSeries)
            }
          />
          <Column
            field="AvailableQuantity"
            header={t('pages.maintenance_orders.available_quantity')}
            body={quantityAvailableTemplate}
          />
          <Column
            body={(rowData) =>
              actionBodyTemplate(
                rowData,
                handleAssignMaterial,
                assigningMaterial,
                maintenanceOrder,
                t
              )
            }
            style={{ textAlign: 'center', width: '5rem' }}
          />
        </DataTable>
      ) : (
        <div className="p-3 text-center text-sm text-600">
          {t('pages.maintenance_orders.no_articles_available')}
        </div>
      )}
    </div>
  );
};


interface MaterialListProps {
  loadingAssignedMaterials: boolean;
  assignedMaterials: MaterialTableDto[];
  maintenanceOrderId: string;
  onRemoveMaterial?: (materialId: string, maintenanceOrderId: string) => void;
  removingMaterialId?: string | null;
  /** Una orden cerrada no admite cambios: se oculta la columna de borrado. */
  readOnly?: boolean;
}

const currency = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
});

export const MaterialList = ({
  loadingAssignedMaterials,
  assignedMaterials,
  maintenanceOrderId,
  onRemoveMaterial,
  removingMaterialId,
  readOnly = false,
}: MaterialListProps) => {
  const { t, i18n } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);

  return (
    <div className="col-12 mb-4">
      <DataTable
        value={assignedMaterials}
        dataKey="id"
        loading={loadingAssignedMaterials}
        paginator={assignedMaterials.length > ROWS_PER_PAGE}
        rows={ROWS_PER_PAGE}
        emptyMessage={t('pages.maintenance_orders.no_assigned_materials')}
        tableStyle={{ tableLayout: 'fixed' }}
        size="small"
        className={TABLE_CLASS}
      >
        <Column
          field="article"
          header={t('pages.maintenance_orders.article')}
          style={{ width: '16rem' }}
          body={(row: MaterialTableDto) =>
            textCell(row.description ?? row.article)
          }
        />

        <Column
          field="series"
          header={t('pages.maintenance_orders.series')}
          style={{ width: '12rem' }}
          body={(row: MaterialTableDto) => textCell(row.series)}
        />

        <Column
          field="quantity"
          header={t('pages.maintenance_orders.quantity')}
          style={{ width: '7rem', textAlign: 'right' }}
          body={(row: MaterialTableDto) => row.quantity ?? 0}
        />

        <Column
          field="unit_cost"
          header={t('pages.maintenance_orders.unit_cost')}
          style={{ width: '9rem', textAlign: 'right' }}
          body={(row: MaterialTableDto) => currency.format(row.unit_cost ?? 0)}
        />

        <Column
          field="total_cost"
          header={t('pages.maintenance_orders.total_cost')}
          style={{ width: '9rem', textAlign: 'right' }}
          body={(row: MaterialTableDto) => currency.format(row.total_cost ?? 0)}
        />

        <Column
          field="date"
          header={t('pages.maintenance_orders.date')}
          style={{ width: '11rem' }}
          body={(row: MaterialTableDto) =>
            textCell(
              formatDateTimeText(row.date, selectedTimezone, i18n.language)
            )
          }
        />

        {!readOnly && (
          <Column
            style={{ width: '6rem', textAlign: 'center' }}
            body={(row: MaterialTableDto) => (
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                onClick={() => onRemoveMaterial?.(row.id, maintenanceOrderId)}
                loading={removingMaterialId === row.id}
                disabled={removingMaterialId != null}
                title={t('pages.maintenance_orders.remove_material')}
              />
            )}
          />
        )}
      </DataTable>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Incidencias asociadas                                                      */
/* -------------------------------------------------------------------------- */

interface AssociatedIncidencesListProps {
  incidences: IncidenceTableDto[];
  loading?: boolean;
  onOpenIncidence: (incidence: IncidenceTableDto) => void;
}

export const AssociatedIncidencesList = ({
  incidences,
  loading = false,
  onOpenIncidence,
}: AssociatedIncidencesListProps) => {
  const { t, i18n } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);
  /** Una sola línea con "…"; el valor completo se ve al pasar el ratón. */
  const oneLineCell = (value?: string | number | null): React.ReactNode =>
    isEmptyValue(value) ? (
      <EmptyValue />
    ) : (
      <div
        className="white-space-nowrap overflow-hidden text-overflow-ellipsis"
        title={String(value)}
      >
        {value}
      </div>
    );

  /** Fecha arriba y hora abajo: ocupa la mitad de ancho que en una línea. */
  const dateTwoLinesCell = (value?: string | Date | null): React.ReactNode => {
    if (!value) return <EmptyValue />;

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return textCell(String(value));

    return (
      <div className="white-space-nowrap">
        <span className="block">{date.toLocaleDateString('es-ES')}</span>
        <span className="text-sm text-600">
          {date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    );
  };
  return (
    <div className="col-12 mb-4">
      <DataTable
        value={incidences}
        dataKey="id"
        loading={loading}
        paginator={incidences.length > ROWS_PER_PAGE}
        rows={ROWS_PER_PAGE}
        emptyMessage={t('pages.maintenance_orders.no_incidences')}
        tableStyle={{ tableLayout: 'fixed' }}
        size="small"
        className={TABLE_CLASS}
        selectionMode="single"
        onSelectionChange={(e) => {
          const row = e.value as IncidenceTableDto | null;
          if (row?.id) onOpenIncidence(row);
        }}
        rowHover
      >
        <Column
          field="incident_code"
          header={t('pages.menu.ID_incident')}
          style={{ width: '8%' }}
        />

        {/* Limitada a 2 líneas para que una descripción larga no estire la fila */}
        <Column
          field="description"
          header={t('pages.incidents.description')}
          style={{ width: '26%' }}
          body={(row: IncidenceTableDto) => clampedCell(row.description)}
        />

        <Column
          field="device"
          header={t('pages.incidents.device_name')}
          style={{ width: '20%' }}
          body={(row: IncidenceTableDto) => oneLineCell(row.device)}
        />

        <Column
          field="status"
          header={t('pages.incidents.status')}
          style={{ width: '10%' }}
          body={(row: IncidenceTableDto) =>
            row.status ? (
              <Tag
                value={row.status}
                style={{
                  backgroundColor: getIncidentStatusColor(row.status),
                  color: '#fff',
                }}
              />
            ) : (
              <EmptyValue />
            )
          }
        />

        <Column
          field="start_time"
          header={t('pages.incidents.start_time')}
          style={{ width: '10%' }}
          body={(row: IncidenceTableDto) => dateTwoLinesCell(formatDateTimeText(
            row.start_time,
            selectedTimezone,
            i18n.language
          ))}
        />
      </DataTable>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Tiempos imputados                                                          */
/* -------------------------------------------------------------------------- */

interface AllocatedTimesListProps {
  allocatedTimes: AllocatedTimeDTO[];
  loading?: boolean;
}

export const AllocatedTimesList = ({
  allocatedTimes,
  loading = false,
}: AllocatedTimesListProps) => {
  const { t, i18n } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);

  // Anchos en porcentaje (suman 100%) para que las proporciones se mantengan
  // en cualquier ancho de pantalla con tableLayout: 'fixed'.
  return (
    <div className="col-12 mb-4">
      <DataTable
        value={allocatedTimes}
        dataKey="id"
        loading={loading}
        paginator={allocatedTimes.length > ROWS_PER_PAGE}
        rows={ROWS_PER_PAGE}
        emptyMessage={t('pages.maintenance_orders.no_allocated_times')}
        tableStyle={{ tableLayout: 'fixed' }}
        size="small"
        className={TABLE_CLASS}
      >
        <Column
          field="employee"
          header={t('pages.maintenance_orders.assigned_user')}
          style={{ width: '24%' }}
          body={(row: AllocatedTimeDTO) => wrapCell(row.employee)}
        />

        <Column
          field="initial_date"
          header={t('pages.maintenance_orders.initial_date')}
          style={{ width: '17%' }}
          body={(row: AllocatedTimeDTO) =>
            textCell(
              formatDateTimeText(
                row.initial_date,
                selectedTimezone,
                i18n.language
              )
            )
          }
        />

        <Column
          field="final_date"
          header={t('pages.maintenance_orders.final_date')}
          style={{ width: '17%' }}
          body={(row: AllocatedTimeDTO) =>
            textCell(
              formatDateTimeText(
                row.final_date,
                selectedTimezone,
                i18n.language
              )
            )
          }
        />

        <Column
          field="duration"
          header={t('pages.maintenance_orders.duration')}
          style={{ width: '10%', textAlign: 'right' }}
          body={(row: AllocatedTimeDTO) => `${row.duration ?? 0} min`}
        />

        <Column
          field="comments"
          header={t('pages.maintenance_orders.comments')}
          style={{ width: '32%' }}
          body={(row: AllocatedTimeDTO) => clampedCell(row.comments)}
        />
      </DataTable>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Ficha de detalle de la orden                                               */
/* -------------------------------------------------------------------------- */

type MaintenanceOrderDetailsCardProps = {
  maintenanceOrder?: MaintenanceOrderDetailsDto | null;
};

const DetailItem = ({
  label,
  value,
  className = 'col-12 md:col-6 lg:col-4',
}: {
  label: string;
  value?: React.ReactNode;
  className?: string;
}) => (
  <div className={`${className} mb-3`}>
    <div className={LABEL_CLASS}>{label}</div>

    {/* Ojo: no usar `value || fallback`. El serial del componente es numérico
        y un 0 legítimo se mostraría como vacío. */}
    <div className={VALUE_CLASS}>
      {isEmptyValue(value) ? <EmptyValue /> : value}
    </div>
  </div>
);

export function MaintenanceOrderDetailsCard({
  maintenanceOrder,
}: MaintenanceOrderDetailsCardProps) {
  const { t, i18n } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);

  if (!maintenanceOrder) {
    return (
      <div className="surface-card border-round-xl shadow-3 p-4 mb-4">
        <div className="text-base text-700">
          {t('pages.maintenance_orders.can_not_load')}
        </div>
      </div>
    );
  }

  const component = maintenanceOrder.associated_component;

  return (
    <div className="surface-card border-round-xl shadow-3 p-4 mb-4">
      <div className="grid">
        <DetailItem
          label={t('pages.maintenance_orders.code')}
          value={maintenanceOrder.code}
        />

        <DetailItem
          label={t('pages.maintenance_orders.description')}
          value={maintenanceOrder.description}
        />

        <DetailItem
          label={t('pages.maintenance_orders.status')}
          value={
            maintenanceOrder.status ? (
              <Tag
                value={maintenanceOrder.status}
                style={{
                  backgroundColor: getIncidentStatusColor(
                    maintenanceOrder.status
                  ),
                  color: '#fff',
                }}
              />
            ) : undefined
          }
        />

        <DetailItem
          label={t('pages.maintenance_orders.type')}
          value={maintenanceOrder.type}
        />

        <DetailItem
          label={t('pages.maintenance_orders.creation_date')}
          value={formatDateTimeText(
            maintenanceOrder.creation_date,
            selectedTimezone,
            i18n.language
          )}
        />

        <DetailItem
          label={t('pages.maintenance_orders.execution_date')}
          value={formatDateTimeText(
            maintenanceOrder.execution_date,
            selectedTimezone,
            i18n.language
          )}
        />

        <DetailItem
          label={t('pages.maintenance_orders.close_date')}
          value={formatDateTimeText(
            maintenanceOrder.close_date,
            selectedTimezone,
            i18n.language
          )}
        />

        <DetailItem
          label={t('pages.maintenance_orders.assigned_user')}
          value={maintenanceOrder.assigned_user}
        />

        <DetailItem
          label={t('pages.maintenance_orders.component_serial_number')}
          // El backend serializa el serial como número y manda 0 cuando no hay
          // componente; ese 0 no es un serial, es un hueco.
          value={
            component?.serial && !Number.isNaN(component.serial)
              ? String(component.serial)
              : undefined
          }
        />

        <DetailItem
          label={t('pages.maintenance_orders.component_description')}
          value={component?.description}
        />

        <DetailItem
          label={t('pages.maintenance_orders.observations')}
          value={maintenanceOrder.observations}
          className="col-12"
        />
      </div>
    </div>
  );
}

export default MaintenanceOrderDetailsCard;
